/** Theme-local category link and accessible disclosure; no shared menu writes. */
class ItalFlexChairNavigation extends HTMLElement {
  #button;
  #panel;
  #observer;

  connectedCallback() {
    this.#observer = new MutationObserver(this.#bindControls);
    this.#observer.observe(this, { childList: true, subtree: true });
    this.#bindControls();
    this.#setOpen(false);
    window.addEventListener('scroll', this.#onScroll, true);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('focusout', this.#onFocusOut);
    document.addEventListener('pointerdown', this.#onOutsidePointer);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onScroll, true);
    this.#observer?.disconnect();
    this.#observer = undefined;
    this.#button?.removeEventListener('click', this.#onToggle);
    this.#button = undefined;
    this.#panel = undefined;
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('focusout', this.#onFocusOut);
    document.removeEventListener('pointerdown', this.#onOutsidePointer);
  }

  #bindControls = () => {
    const button = this.querySelector('.ifx-chair-nav__toggle');
    const panel = this.querySelector('.ifx-chair-nav__links');
    if (button === this.#button && panel === this.#panel) return;
    this.#button?.removeEventListener('click', this.#onToggle);
    this.#button = button;
    this.#panel = panel;
    if (!button || !panel) return;
    button.hidden = false;
    button.addEventListener('click', this.#onToggle);
  };

  #setOpen(open) {
    if (!this.#button || !this.#panel) return;
    if (open && this.dataset.variant === 'desktop') {
      const rect = this.querySelector('.ifx-chair-nav__trigger').getBoundingClientRect();
      this.#panel.style.setProperty('--ifx-chair-panel-top', `${rect.bottom}px`);
      this.#panel.style.setProperty('--ifx-chair-panel-left', `${Math.max(8, Math.min(rect.left - 12, innerWidth - 206))}px`);
    }
    this.#button.setAttribute('aria-expanded', String(open));
    this.#panel.hidden = !open;
  }

  #onScroll = () => { if (this.dataset.variant === 'desktop') this.#setOpen(false); };

  #onToggle = () => this.#setOpen(this.#button.getAttribute('aria-expanded') !== 'true');

  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#button || !this.#panel || this.#panel.hidden) return;
    event.preventDefault();
    event.stopPropagation();
    this.#setOpen(false);
    this.#button.focus();
  };

  #onFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && !this.contains(event.relatedTarget)) this.#setOpen(false);
  };

  #onOutsidePointer = (event) => {
    if (event.target instanceof Node && !this.contains(event.target)) this.#setOpen(false);
  };

}

if (!customElements.get('italflex-chair-navigation')) {
  customElements.define('italflex-chair-navigation', ItalFlexChairNavigation);
}


// Globo may replace the native list after hydration. Move only the added item;
// never rebuild or mutate the existing merchant links/app configuration.
const chairMenuMounts = new Map();
let chairMenuPlacementQueued = false;
const normalizedMenuText = (value) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
function chairMenuPlacement() {
  chairMenuPlacementQueued = false;
  const header = document.getElementById('header-component');
  if (!header) return;
  for (const item of header.querySelectorAll('[data-ifx-chair-menu-item]')) {
    chairMenuMounts.set(item.id, item);
  }
  const currentMountIds = new Set();
  for (const backup of header.querySelectorAll('template[data-ifx-chair-menu-backup]')) {
    const source = backup.content.querySelector('[data-ifx-chair-menu-item]');
    if (!source?.id) continue;
    currentMountIds.add(source.id);
    if (!chairMenuMounts.has(source.id)) {
      chairMenuMounts.set(source.id, source.cloneNode(true));
    }
  }
  for (const id of chairMenuMounts.keys()) {
    if (!currentMountIds.has(id)) chairMenuMounts.delete(id);
  }
  for (const item of chairMenuMounts.values()) {
    const mobile = item.dataset.chairMenuVariant === 'mobile';
    const host = mobile
      ? header.querySelector('.menu-drawer__navigation .gm-menu-mobile') ?? header.querySelector('.menu-drawer__navigation > ul')
      : header.querySelector('header-menu nav.gm-menu:not(.gm-menu-mobile)') ?? header.querySelector('header-menu overflow-list');
    if (!host) continue;
    const globo = host.classList.contains('gm-menu') || host.classList.contains('gm-menu-mobile');
    item.classList.toggle('gm-item', globo);
    item.classList.toggle('gm-level-0', globo);
    item.dataset.menuEngine = globo ? 'globo' : 'native';
    if (globo) { item.removeAttribute('slot'); item.removeAttribute('aria-hidden'); }
    const siblings = Array.from(host.children).filter((element) => element !== item && !element.hasAttribute('data-ifx-chair-menu-item'));
    const anchorOf = (element) => element.querySelector('a[href]');
    const existing = siblings.some((element) => {
      const anchor = anchorOf(element);
      return anchor && (normalizedMenuText(anchor.textContent) === 'scaune si fotolii' || anchor.href.includes('view=chairs-review') || /\/collections\/scaune-si-fotolii(?:[/?#]|$)/.test(anchor.href));
    });
    item.hidden = existing;
    if (existing) continue;
    const bedroom = siblings.find((element) => normalizedMenuText(anchorOf(element)?.textContent ?? '') === 'dormitor');
    const hallway = siblings.find((element) => normalizedMenuText(anchorOf(element)?.textContent ?? '') === 'hol');
    const reference = anchorOf(bedroom ?? hallway ?? siblings.find((element) => anchorOf(element)) ?? host);
    if (reference) {
      const style = getComputedStyle(reference);
      // Variable fonts can serialize computed style.font as an empty string.
      // Copy the actual reference longhands instead of guessing a font size.
      for (const [property, value] of Object.entries({
        '--ifx-chair-label-font-family': style.fontFamily,
        '--ifx-chair-label-font-size': style.fontSize,
        '--ifx-chair-label-font-weight': style.fontWeight,
        '--ifx-chair-label-font-style': style.fontStyle,
        '--ifx-chair-label-line-height': style.lineHeight,
      })) {
        if (value) item.style.setProperty(property, value);
      }
      if (globo && !mobile) {
        const referenceHeight = reference.getBoundingClientRect().height;
        if (referenceHeight > 0) item.style.setProperty('--ifx-chair-link-height', `${referenceHeight}px`);
      }
      item.style.setProperty('--ifx-chair-label-color', style.color);
      if (globo && !mobile) item.style.setProperty('--ifx-chair-link-inset', style.paddingLeft);
    }
    if (bedroom) {
      if (bedroom.nextElementSibling !== item) bedroom.after(item);
    } else if (hallway) {
      if (hallway.previousElementSibling !== item) hallway.before(item);
    } else {
      const more = host.querySelector(':scope > [slot="more"]');
      if (more && more.previousElementSibling !== item) more.before(item);
      else if (!more && host.lastElementChild !== item) host.append(item);
    }
  }
}
const queueChairMenuPlacement = () => {
  if (chairMenuPlacementQueued) return;
  chairMenuPlacementQueued = true;
  requestAnimationFrame(chairMenuPlacement);
};
const chairMenuReplacementObserver = new MutationObserver((records) => {
  const header = document.getElementById('header-component');
  if (records.some((record) => header?.contains(record.target) || Array.from(record.addedNodes).some((node) => node instanceof Element && (node.id === 'header-component' || node.querySelector('#header-component'))))) {
    queueChairMenuPlacement();
  }
});
chairMenuReplacementObserver.observe(document.documentElement, { childList: true, subtree: true });
chairMenuPlacement();
